import React from 'react';
import {
  Alert,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Typography,
} from 'antd';
import { ShoppingCartOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { OrderOptionDto } from '../dto/order';
import type { TransportRouteDetailDto } from '../dto/transport';
import { isPreConfirmationOrderStatus } from '../modules/orders/orderStatusTransitions';
import TransportCapacityBadge from './TransportCapacityBadge';
import TransportCargoItemCard from './TransportCargoItemCard';
import TransportFormSectionTitle from './TransportFormSectionTitle';
import TransportSplitTable from './TransportSplitTable';
import type { CargoTableRow } from './sharedTransportFormRows';
import {
  findStaleCargoQuantityWarnings,
  formatStaleCargoQuantityWarningItems,
  type CargoHintProvenanceState,
  type SharedTransportCargoItem,
  type TrailerCapacityIndicator,
} from './sharedTransportFormUtils';
import {
  type AllocationMethod as CostAllocationMethod,
  type TransportCostAllocationItemResult,
} from '../utils/transportCostAllocation';
import type {
  TransportSplitProduct,
  TransportSplitRow,
} from '../utils/transportSplit';
import './TransportOrdersCargoSection.css';

const { Text } = Typography;

interface CargoStatItem {
  key: string;
  label: string;
  value: string;
}

interface TransportOrdersCargoSectionProps {
  transport?: TransportRouteDetailDto | null;
  onSubmitSplit?: (payload: Record<string, unknown>) => Promise<void>;
  canEditTransportCosts: boolean;
  availableOrders: OrderOptionDto[];
  availableColumns: ColumnsType<OrderOptionDto>;
  orderRowSelection: {
    selectedRowKeys: string[];
    onChange: (keys: React.Key[]) => void;
  };
  cargoRows: CargoTableRow[];
  currency?: string;
  costAllocationMethod: CostAllocationMethod;
  showUnitCosts: boolean;
  onShowUnitCostsChange: (checked: boolean) => void;
  allocationItemsById: Map<string | undefined, TransportCostAllocationItemResult>;
  formatCurrencyAmount: (value: number | null | undefined) => string;
  // Per-cargo-row supplier purchase-value provenance (keyed by row.key). Drives
  // the info/warning icon next to the "Wartość towaru" input. Empty map = no
  // supplier hints available, so nothing extra renders (today's behavior).
  cargoHintProvenanceByKey: Record<string, CargoHintProvenanceState>;
  // Called when the operator edits a goods-value cell so the parent stops
  // prefilling that line from the supplier hint.
  onGoodsValueTouched: (key: string) => void;
  onRemoveCargo: (key: string) => void;
  cargoStatItems: CargoStatItem[];
  // F1 (trailer capacity indicator): display-only, never blocks save.
  ldmCapacity: TrailerCapacityIndicator | null;
  weightCapacity: TrailerCapacityIndicator | null;
  hasMissingDimensions: boolean;
  splitProducts: Array<TransportSplitProduct & SharedTransportCargoItem>;
  splitEnabled: boolean;
  onSplitEnabledChange: (checked: boolean) => void;
  splitCount: number;
  onSplitCountChange: (value: number | null) => void;
  splitRows: TransportSplitRow[];
  onSplitRowsChange: (rows: TransportSplitRow[]) => void;
  splitHasSumMismatch: boolean;
  splitHasEmptyTruck: boolean;
}

const TransportOrdersCargoSection: React.FC<TransportOrdersCargoSectionProps> = ({
  transport,
  onSubmitSplit,
  canEditTransportCosts,
  availableOrders,
  availableColumns,
  orderRowSelection,
  cargoRows,
  currency,
  costAllocationMethod,
  showUnitCosts,
  onShowUnitCostsChange,
  allocationItemsById,
  formatCurrencyAmount,
  cargoHintProvenanceByKey,
  onGoodsValueTouched,
  onRemoveCargo,
  cargoStatItems,
  ldmCapacity,
  weightCapacity,
  hasMissingDimensions,
  splitProducts,
  splitEnabled,
  onSplitEnabledChange,
  splitCount,
  onSplitCountChange,
  splitRows,
  onSplitRowsChange,
  splitHasSumMismatch,
  splitHasEmptyTruck,
}) => {
  const { t } = useTranslation(['transport', 'common']);

  // Owner-approved: transport for an unconfirmed order stays fully allowed —
  // no role/status gate, no save blocking — but the form shows a non-blocking
  // informational warning so the operator double-checks the customer actually
  // confirmed. isPreConfirmationOrderStatus is the single home for the status
  // set (NEW_INQUIRY/QUOTE_SENT/AWAITING_CONFIRMATION + legacy
  // QUOTE_REQUESTED/PROFORMA_SENT). Uses the same displayOrderNumber the order
  // picker's own column renders, never the raw id.
  const selectedRowKeySet = new Set(orderRowSelection.selectedRowKeys);
  const unconfirmedSelectedOrderNumbers = availableOrders
    .filter((order) => selectedRowKeySet.has(order.id) && isPreConfirmationOrderStatus(order.status))
    .map((order) => order.displayOrderNumber || order.orderNumber);

  // HOTEL (HIGH, "warn, never block"): a persisted cargo line's loaded
  // quantity can exceed the order product's CURRENT quantity when the order
  // was edited down after this transport was built. Edit-mode only
  // (`transport` is only set once editing an existing transport). Non-blocking
  // — the existing per-line overflow guard (which blocks save, further down in
  // this component) is a separate, unrelated check and stays exactly as-is.
  const staleQuantityWarnings = transport ? findStaleCargoQuantityWarnings(transport) : [];

  return (
    <Card
      className="transport-orders-cargo-card"
      title={<TransportFormSectionTitle step={1} icon={<ShoppingCartOutlined />} label={t('transport:sections.ordersCargo')} />}
    >
      <Table<OrderOptionDto>
        className="transport-order-picker-table"
        data-testid="transport-order-picker-table"
        rowKey="id"
        size="small"
        pagination={{ pageSize: 8 }}
        rowSelection={orderRowSelection}
        dataSource={availableOrders}
        columns={availableColumns}
      />

      {unconfirmedSelectedOrderNumbers.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          message={unconfirmedSelectedOrderNumbers.length === 1
            ? t('transport:cargo.unconfirmedOrderWarningSingular', { number: unconfirmedSelectedOrderNumbers[0] })
            : t('transport:cargo.unconfirmedOrderWarningPlural', { numbers: unconfirmedSelectedOrderNumbers.join(', ') })}
          style={{ marginTop: 16 }}
        />
      ) : null}

      {/* HOTEL (HIGH, "warn, never block"): the order changed since this
          transport's cargo was built (e.g. quantity reduced) — non-blocking,
          no auto-resync, save is NOT blocked by this Alert. */}
      {staleQuantityWarnings.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          message={t('transport:cargo.staleQuantityWarningTitle')}
          description={(
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {formatStaleCargoQuantityWarningItems(staleQuantityWarnings, t).map((line, index) => (
                <li key={staleQuantityWarnings[index].orderProductId}>{line}</li>
              ))}
            </ul>
          )}
          style={{ marginTop: 16 }}
        />
      ) : null}

      <Divider orientation="left">{t('transport:cargo.loadedItems')}</Divider>
      {cargoRows.length === 0 ? (
        // Friendly empty state instead of a bare table with a validation
        // sentence — no orders selected yet, nothing to load.
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('transport:cargo.emptyStateHint')}
          style={{ margin: '24px 0' }}
        />
      ) : (
        <>
          {canEditTransportCosts ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <Space size={8}>
                <Switch
                  id="transport-unit-costs-toggle"
                  checked={showUnitCosts}
                  onChange={onShowUnitCostsChange}
                  size="small"
                  aria-label={t('transport:unitCostsToggle')}
                />
                <label htmlFor="transport-unit-costs-toggle">{t('transport:unitCostsToggle')}</label>
              </Space>
            </div>
          ) : null}
          <div
            data-testid="transport-cargo-list"
            style={{ display: 'grid', gap: 12, minWidth: 0, width: '100%' }}
          >
            {cargoRows.map((record) => (
              <TransportCargoItemCard
                key={record.key}
                record={record}
                canEditTransportCosts={canEditTransportCosts}
                currency={currency}
                costAllocationMethod={costAllocationMethod}
                showUnitCosts={showUnitCosts}
                allocationItem={allocationItemsById.get(record.id || record.key)}
                formatCurrencyAmount={formatCurrencyAmount}
                hintProvenance={cargoHintProvenanceByKey[record.key]}
                onGoodsValueTouched={onGoodsValueTouched}
                onRemoveCargo={onRemoveCargo}
              />
            ))}
          </div>
        </>
      )}

      {/* f. Szczegóły ładunku — compact read-only Statistic row (auto-computed) */}
      <Row gutter={[24, 8]} style={{ marginTop: 20 }}>
        {cargoStatItems.map((item) => (
          <Col key={item.key} xs={12} sm={6}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 12 }}>{item.label}</Text>}
              value={item.value}
              valueStyle={{ fontSize: 16 }}
            />
          </Col>
        ))}
        {hasMissingDimensions ? (
          <Col xs={24}>
            <Text type="warning" style={{ fontSize: 12 }}>
              {t('transport:cargo.missingDimensionsHint')}
            </Text>
          </Col>
        ) : null}
      </Row>

      {/* F1 (trailer capacity indicator): display-only utilization vs a
          standard trailer — never blocks save. Weight badge only renders when
          totalWeight > 0 (most lines have no grossWeight entered today, so a
          permanent "0 / 24000 kg" tile would be pure noise). */}
      {(ldmCapacity || weightCapacity) ? (
        <Row gutter={[24, 8]} style={{ marginTop: 8 }}>
          {ldmCapacity ? (
            <Col>
              <TransportCapacityBadge indicator={ldmCapacity} unitLabel="LDM" precision={2} />
            </Col>
          ) : null}
          {weightCapacity ? (
            <Col>
              <TransportCapacityBadge indicator={weightCapacity} unitLabel="kg" precision={0} />
            </Col>
          ) : null}
        </Row>
      ) : null}

      {/* Hidden-but-registered auto-computed fields — the payload keeps them,
          so the read-only stat row above is purely presentational. */}
      <Form.Item name="packages" hidden noStyle>
        <InputNumber />
      </Form.Item>
      <Form.Item name="totalWeight" hidden noStyle>
        <InputNumber />
      </Form.Item>
      <Form.Item name="totalVolume" hidden noStyle>
        <InputNumber />
      </Form.Item>
      <Form.Item name="dimensions" hidden noStyle>
        <Input />
      </Form.Item>

      {!transport && onSubmitSplit && splitProducts.length > 0 ? (
        <div style={{ marginTop: 24 }}>
          <Divider />
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space align="center">
              <Switch
                checked={splitEnabled}
                onChange={onSplitEnabledChange}
                checkedChildren={t('common:yes')}
                unCheckedChildren={t('common:no')}
              />
              <Text>{t('transport:split.toggle')}</Text>
            </Space>
            {splitEnabled ? (
              <>
                <Form.Item label={t('transport:split.count')} style={{ maxWidth: 240, marginBottom: 0 }}>
                  <InputNumber
                    min={2}
                    max={20}
                    precision={0}
                    value={splitCount}
                    onChange={onSplitCountChange}
                    style={{ width: '100%' }}
                    data-testid="transport-split-count"
                  />
                </Form.Item>
                <TransportSplitTable
                  products={splitProducts}
                  value={splitRows}
                  canEditTransportCosts={canEditTransportCosts}
                  currency={currency}
                  onChange={onSplitRowsChange}
                />
                {splitHasSumMismatch ? (
                  <Alert
                    type="error"
                    showIcon
                    message={t('transport:split.sumMismatch')}
                  />
                ) : null}
                {splitHasEmptyTruck ? (
                  <Alert
                    type="error"
                    showIcon
                    message={t('transport:split.emptyTruck')}
                  />
                ) : null}
              </>
            ) : null}
          </Space>
        </div>
      ) : null}
    </Card>
  );
};

export default TransportOrdersCargoSection;
